import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Descendant, Editor, Element as SlateElement, Transforms } from 'slate'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { VariableTextFeatureFlag } from '@makyoapp/makyo-common'

import { parseVariableText } from '@/core/app-builder/parser/variable-text'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useUpdateCompSettingValue } from '@/hooks/useUpdateCompSettings'
import { BuilderComponentSetting, BuilderComponentSettingValidation } from '@/shared/builder'
import { getValidationFn } from '@/shared/builder-utils'
import { VariableTextV2 } from '@/components/VariableTextV2/VariableTextV2'

interface VariableTextFieldSettingProps {
  id: string
  value: any
  validation?: BuilderComponentSettingValidation
  rawPlaceholder?: string
  variableTextSettings?: BuilderComponentSetting['variableTextSettings']
  settingId?: string
}

const VariableTextFieldSetting = ({
  id,
  value,
  validation,
  rawPlaceholder,
  variableTextSettings,
  settingId,
}: VariableTextFieldSettingProps) => {
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const [charCount, setCharCount] = useState(0)

  const { userEmail } = useAppAuthContext()
  const { dbClient } = useDbClientContext()
  const { activeComponent, activeLayerClient, activeLayerElementSettings } = useActiveLayerContext()
  const activeComponentId = activeComponent?.id || ''

  const updateValue = useUpdateCompSettingValue(id)

  const { flags = 0, tableSourceId, columnTypes, shouldUpdateComponentTitle, maxChars } = variableTextSettings ?? {}
  const validationFn = validation ? getValidationFn(validation) : undefined
  const placeholder = rawPlaceholder ? t(rawPlaceholder) : undefined
  const variableColumnOptions = {
    columnTypes: columnTypes || Object.values(ColumnTypes),
  }

  let varValue = value
  if (!Array.isArray(varValue)) {
    varValue = undefined
  } else {
    if (!(varValue as Record<string, unknown>[])[0]?.type) {
      varValue = undefined
    }
  }

  // Initialize and update character count when value changes
  useEffect(() => {
    if (!maxChars) return

    if (varValue) {
      const text = parseVariableText(varValue as Descendant[], dbClient!, activeLayerClient, userEmail)
      setCharCount(text.length)
    } else {
      setCharCount(0)
    }
  }, [varValue, dbClient, activeLayerClient, userEmail, maxChars])

  const handleValidation = useCallback(
    async (value: Descendant[]) => {
      const text = parseVariableText(value, dbClient!, activeLayerClient, userEmail)

      // Check maxChars validation
      if (maxChars && text.length > maxChars) {
        setError(t('Maximum {{maxChars}} characters allowed', { maxChars }))
        return
      }

      // Check other validations
      if (validationFn) {
        setError(validationFn(text))
      } else {
        setError('')
      }
    },
    [dbClient, activeLayerClient, userEmail, validationFn, maxChars, t]
  )

  const handleRename = useCallback(
    (componentId: string, newName: string) => {
      if (!activeLayerClient) return

      const component = activeLayerClient.componentsMap.get(componentId)
      if (!component) return

      component.Rename(newName)
    },
    [activeLayerClient]
  )

  const handleChange = useCallback(
    (value: Descendant[]) => {
      const text = parseVariableText(value, dbClient!, activeLayerClient, userEmail)

      // Prevent changes if maxChars is exceeded
      if (maxChars && text.length > maxChars) {
        return
      }

      updateValue(value)
      setCharCount(text.length)

      if (shouldUpdateComponentTitle) {
        handleRename(activeComponentId, text)
      }

      // Validate on change
      handleValidation(value)
    },
    [
      updateValue,
      shouldUpdateComponentTitle,
      activeComponentId,
      activeLayerClient,
      userEmail,
      handleRename,
      handleValidation,
      maxChars,
      dbClient,
    ]
  )

  return (
    <div>
      <VariableTextV2
        id={id}
        validation={handleValidation}
        tableId={
          tableSourceId ? (activeLayerElementSettings?.[activeComponentId]?.[tableSourceId] as string) : undefined
        }
        placeholder={placeholder}
        value={varValue as Descendant[]}
        featureFlags={flags as VariableTextFeatureFlag}
        variableColumnOptions={variableColumnOptions}
        onChange={handleChange}
        settingId={settingId}
      />
      {maxChars && (
        <div className='mk-input-desc'>
          {charCount}/{maxChars} {t('characters')}
        </div>
      )}
      {error && <div className='mk-input-error'>{error}</div>}
    </div>
  )
}

export default VariableTextFieldSetting
